import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, FileText, Download, Printer, Share2, Pencil, Eye, Loader2, X, Receipt, Save, Wallet, QrCode, Building2, FileSignature, AlertCircle, Settings, Upload, Image as ImageIcon, Trash2, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import WhatsAppSendDialog from '../../components/WhatsAppSendDialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
const METHODS = ['Cash', 'UPI', 'Razorpay', 'Cheque / DD', 'NEFT / Bank Transfer', 'Card'];
const EMPTY = { customer_name: '', customer_phone: '', payment_date: new Date().toISOString().slice(0, 10), receipt_number: '', service_booking_id: '', service_description: '', amount: '', payment_method: 'Cash', transaction_id: '', notes: '' };

const SETTINGS_PATH = '/super-admin/non-gst-settings';
const METHOD_OPTIONS = [
  { id: 'upi', label: 'UPI / QR' },
  { id: 'razorpay', label: 'Razorpay (Cards / NetBanking)' },
  { id: 'cash', label: 'Cash' },
  { id: 'cheque', label: 'Cheque / DD' },
  { id: 'neft', label: 'NEFT / Bank Transfer' },
];

export default function NonGstPayments({ api }) {
  const [view, setView] = useState('settings'); // 'settings' (customise receipt) | 'receipts' (records)

  return (
    <div className="space-y-4" data-testid="non-gst-payments">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="ngp-subnav">
        <button onClick={() => setView('settings')} className={`px-4 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 ${view === 'settings' ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="ngp-nav-settings"><Settings size={14} /> Receipt Settings</button>
        <button onClick={() => setView('receipts')} className={`px-4 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 ${view === 'receipts' ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="ngp-nav-receipts"><Receipt size={14} /> Receipts</button>
      </div>

      {view === 'settings' ? <NonGstSettings api={api} /> : <NonGstReceipts api={api} />}
    </div>
  );
}

function NonGstReceipts({ api }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [waSend, setWaSend] = useState(null);
  const [waEnabled, setWaEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (range.from) params.set('date_from', range.from);
      if (range.to) params.set('date_to', range.to);
      const qs = params.toString();
      const r = await api('GET', `/admin/non-gst-receipts${qs ? `?${qs}` : ''}`);
      setRows(r.data.receipts || []);
      setSummary({ count: r.data.count || 0, total_amount: r.data.total_amount || 0 });
    } catch (e) { toast.error('Failed to load receipts'); }
    finally { setLoading(false); }
  }, [api, q, range]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    api('GET', '/admin/whatsapp/config')
      .then((r) => setWaEnabled(!!r.data?.send_buttons_enabled && (r.data?.accounts || []).some((a) => a.enabled && a.has_key)))
      .catch(() => setWaEnabled(false));
  }, [api]);

  // Authenticated PDF fetch -> blob URL (works for preview/download/print/share)
  const fetchPdfBlob = async (rid) => {
    const token = localStorage.getItem('entorid_panel_token');
    const res = await fetch(`${BACKEND}/api/panel/admin/non-gst-receipts/${rid}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('PDF failed');
    return res.blob();
  };

  const doPreview = async (row) => {
    try { const blob = await fetchPdfBlob(row.id); setPreview({ url: URL.createObjectURL(blob), row }); }
    catch { toast.error('Could not open preview'); }
  };
  const doDownload = async (row) => {
    try { const blob = await fetchPdfBlob(row.id); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${row.receipt_number}.pdf`; a.click(); }
    catch { toast.error('Download failed'); }
  };
  const doPrint = async (row) => {
    try { const blob = await fetchPdfBlob(row.id); const u = URL.createObjectURL(blob); const w = window.open(u); if (w) w.onload = () => w.print(); }
    catch { toast.error('Print failed'); }
  };
  const doShare = async (row) => {
    try {
      const blob = await fetchPdfBlob(row.id);
      const file = new File([blob], `${row.receipt_number}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: row.receipt_number, text: `Payment receipt ${row.receipt_number}` });
      } else { await doDownload(row); toast.message('Sharing not supported — downloaded instead'); }
    } catch (e) { if (e?.name !== 'AbortError') toast.error('Share failed'); }
  };

  return (
    <div className="space-y-4" data-testid="non-gst-receipts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Receipt size={20} className="text-emerald-600" /> Non-GST Payments</h2>
          <p className="text-sm text-slate-500">Bill-of-supply receipts for non-GST transactions — separate numbering &amp; records from GST invoices.</p>
        </div>
        <Button onClick={() => setEditing(EMPTY)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ngp-new"><Plus size={15} className="mr-1" /> New Receipt</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-emerald-600 text-white p-3"><div className="text-[11px] uppercase text-emerald-100">Receipts</div><div className="text-2xl font-extrabold">{summary.count}</div></div>
        <div className="rounded-xl bg-white border border-slate-200 p-3"><div className="text-[11px] uppercase text-slate-400">Total Collected</div><div className="text-2xl font-extrabold text-slate-900">{inr(summary.total_amount)}</div></div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px] relative">
          <Search size={14} className="absolute left-2.5 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, receipt #, booking, txn…" className="pl-8 h-9" data-testid="ngp-search" />
        </div>
        <div><Label className="text-xs">From</Label><Input type="date" value={range.from} onChange={(e) => setRange((x) => ({ ...x, from: e.target.value }))} className="h-9 w-40" data-testid="ngp-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={range.to} onChange={(e) => setRange((x) => ({ ...x, to: e.target.value }))} className="h-9 w-40" data-testid="ngp-to" /></div>
        {(q || range.from || range.to) && <Button variant="outline" size="sm" onClick={() => { setQ(''); setRange({ from: '', to: '' }); }} data-testid="ngp-clear">Clear</Button>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-14 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-14 text-slate-400" data-testid="ngp-empty"><FileText className="mx-auto mb-2 text-slate-300" size={34} /><p>No non-GST receipts yet.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr><th className="px-3 py-2.5 text-left">Receipt #</th><th className="px-3 py-2.5 text-left">Date</th><th className="px-3 py-2.5 text-left">Customer</th><th className="px-3 py-2.5 text-left">Service</th><th className="px-3 py-2.5 text-right">Amount</th><th className="px-3 py-2.5 text-left">Method</th><th className="px-3 py-2.5 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`ngp-row-${r.id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs font-semibold text-emerald-700">{r.receipt_number}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.payment_date}</td>
                  <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{r.customer_name}</div><div className="text-xs text-slate-400">{r.customer_phone || ''}</div></td>
                  <td className="px-3 py-2.5 text-slate-600 max-w-[200px] truncate">{r.service_description}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900">{inr(r.amount)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.payment_method}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button title="Preview" onClick={() => doPreview(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid={`ngp-preview-${r.id}`}><Eye size={14} /></button>
                      <button title="Download" onClick={() => doDownload(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid={`ngp-download-${r.id}`}><Download size={14} /></button>
                      <button title="Print" onClick={() => doPrint(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid={`ngp-print-${r.id}`}><Printer size={14} /></button>
                      <button title="Share" onClick={() => doShare(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid={`ngp-share-${r.id}`}><Share2 size={14} /></button>
                      {waEnabled && <button title="Send on WhatsApp (AiSensy)" onClick={() => setWaSend(r)} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" data-testid={`ngp-wasend-${r.id}`}><MessageCircle size={14} /></button>}
                      <button title="Edit" onClick={() => setEditing(r)} className="p-1.5 rounded hover:bg-slate-100 text-emerald-700" data-testid={`ngp-edit-${r.id}`}><Pencil size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <ReceiptEditor api={api} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {waSend && <WhatsAppSendDialog api={api} docType="non_gst_receipt" docId={waSend.id} docLabel={`Receipt ${waSend.receipt_number}`} defaultPhone={waSend.customer_phone} onClose={() => setWaSend(null)} />}
      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { URL.revokeObjectURL(preview.url); setPreview(null); } }} data-testid="ngp-preview-modal">
          <div className="bg-white rounded-xl w-full max-w-3xl h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <div className="font-semibold text-slate-800">{preview.row.receipt_number}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => doDownload(preview.row)}><Download size={14} className="mr-1" /> Download</Button>
                <Button size="sm" variant="outline" onClick={() => doPrint(preview.row)}><Printer size={14} className="mr-1" /> Print</Button>
                <button onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} className="p-1.5 text-slate-400 hover:text-slate-600" data-testid="ngp-preview-close"><X size={18} /></button>
              </div>
            </div>
            <iframe title="receipt" src={preview.url} className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiptEditor({ api, initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const autofill = async () => {
    if (!form.service_booking_id?.trim()) return;
    try {
      const r = await api('GET', `/admin/non-gst-receipts/booking-lookup?ref=${encodeURIComponent(form.service_booking_id.trim())}`);
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || r.data.customer_name || '',
        customer_phone: f.customer_phone || r.data.customer_phone || '',
        amount: f.amount || r.data.amount || '',
        service_description: f.service_description || r.data.service_description || '',
      }));
      toast.success('Auto-filled from booking');
    } catch { toast.message('No booking found for that ID'); }
  };

  const save = async () => {
    setErr('');
    if (!form.customer_name.trim()) return setErr('Customer name is required');
    if (!form.service_description.trim()) return setErr('Service description is required');
    if (!form.payment_date) return setErr('Payment date is required');
    if (form.amount === '' || Number(form.amount) < 0) return setErr('Enter a valid amount');
    setSaving(true);
    const payload = { ...form, amount: Number(form.amount) };
    if (isNew && !payload.receipt_number) delete payload.receipt_number;
    try {
      if (isNew) await api('POST', '/admin/non-gst-receipts', payload);
      else await api('PATCH', `/admin/non-gst-receipts/${initial.id}`, payload);
      toast.success(isNew ? 'Receipt created' : 'Receipt updated');
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ngp-editor">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-emerald-700 text-white px-5 py-3.5 flex items-center justify-between rounded-t-2xl">
          <div className="font-bold flex items-center gap-2"><Receipt size={18} /> {isNew ? 'New Non-GST Receipt' : `Edit ${initial.receipt_number}`}</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="ngp-editor-close"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Customer Name *</Label><Input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} data-testid="ngp-f-name" /></div>
          <div><Label>Contact Number</Label><Input value={form.customer_phone} onChange={(e) => set('customer_phone', e.target.value)} data-testid="ngp-f-phone" /></div>
          <div><Label>Payment Date *</Label><Input type="date" value={(form.payment_date || '').slice(0, 10)} onChange={(e) => set('payment_date', e.target.value)} data-testid="ngp-f-date" /></div>
          <div><Label>Receipt Number</Label><Input value={form.receipt_number} onChange={(e) => set('receipt_number', e.target.value)} placeholder={isNew ? 'Auto-generated if blank' : ''} disabled={!isNew} data-testid="ngp-f-number" /></div>
          <div className="sm:col-span-2">
            <Label>Service / Booking ID</Label>
            <div className="flex gap-2">
              <Input value={form.service_booking_id} onChange={(e) => set('service_booking_id', e.target.value)} placeholder="Booking ID or code (optional)" data-testid="ngp-f-booking" />
              <Button type="button" variant="outline" onClick={autofill} data-testid="ngp-f-autofill">Auto-fill</Button>
            </div>
          </div>
          <div className="sm:col-span-2"><Label>Service Description *</Label><Input value={form.service_description} onChange={(e) => set('service_description', e.target.value)} data-testid="ngp-f-desc" /></div>
          <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="ngp-f-amount" /></div>
          <div>
            <Label>Payment Method</Label>
            <select value={form.payment_method} onChange={(e) => set('payment_method', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="ngp-f-method">
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><Label>Transaction ID</Label><Input value={form.transaction_id} onChange={(e) => set('transaction_id', e.target.value)} data-testid="ngp-f-txn" /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="ngp-f-notes" /></div>
          {err && <div className="sm:col-span-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="ngp-editor-error">{err}</div>}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex justify-end gap-2 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} data-testid="ngp-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ngp-editor-save">
            {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving…</> : (isNew ? 'Create Receipt' : 'Save Changes')}
          </Button>
        </div>
      </div>
    </div>
  );
}


function NonGstSettings({ api }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api('GET', SETTINGS_PATH);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load Non-GST settings');
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleMethod = (id) => setData((d) => {
    const cur = new Set(d.payment_methods || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    return { ...d, payment_methods: Array.from(cur) };
  });

  // Upload an image asset (logo / signature / stamp / qr) to Object Storage and store its URL on the matching field.
  const uploadAsset = async (kind, field, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    setUploading(kind);
    try {
      const token = localStorage.getItem('entorid_panel_token');
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const res = await fetch(`${BACKEND}/api/panel/super-admin/non-gst-settings/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || 'Upload failed'); }
      const j = await res.json();
      set(field, j.url);
      toast.success('Image uploaded — click Save Changes to apply');
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setUploading(''); }
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api('PATCH', SETTINGS_PATH, data);
      setData(r.data);
      setSavedMsg('Saved — new Non-GST receipts will use these details.');
      setTimeout(() => setSavedMsg(null), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!data) return <div className="p-8 text-slate-500" data-testid="ngs-loading">Loading Non-GST settings…</div>;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="ngs-settings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet size={22} className="text-emerald-600" /> Non-GST Receipt Settings
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Controls the business details, UPI/QR, bank, signatures and stamp that appear on every Non-GST receipt (Bill of Supply) PDF — updates apply immediately.
          </p>
        </div>
        <button onClick={save} disabled={saving} data-testid="ngs-save"
          className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md disabled:opacity-60">
          <Save size={16} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {savedMsg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-md text-sm" data-testid="ngs-saved">{savedMsg}</div>}
      {err && <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2 rounded-md text-sm flex gap-2 items-center" data-testid="ngs-error"><AlertCircle size={14} /> {err}</div>}

      <Section title="Brand Logo (prints at the top of the receipt)" icon={<ImageIcon size={16} />}>
        <div className="max-w-md" data-testid="ngs-logo-field">
          <ImageField value={data.logo_url} onUpload={(f) => uploadAsset('logo', 'logo_url', f)} onClear={() => set('logo_url', '')}
            uploading={uploading === 'logo'} testid="ngs-logo" hint="PNG/JPG, max 5MB — appears above your company name on every Non-GST receipt." />
        </div>
      </Section>

      <Section title="Company Identity" icon={<Building2 size={16} />}>
        <Grid>
          <F label="Company Name"><I value={data.company_name} onChange={(v) => set('company_name', v)} testid="ngs-company" /></F>
          <F label="Brand Short (logo text)"><I value={data.brand_short} onChange={(v) => set('brand_short', v)} maxLength={6} testid="ngs-brand-short" /></F>
          <F label="Email"><I value={data.email} onChange={(v) => set('email', v)} testid="ngs-email" /></F>
          <F label="Phone (Primary)"><I value={data.phone_primary} onChange={(v) => set('phone_primary', v)} testid="ngs-phone" /></F>
          <F label="Phone (Secondary)"><I value={data.phone_secondary || ''} onChange={(v) => set('phone_secondary', v)} testid="ngs-phone2" /></F>
          <F label="Address Line 1"><I value={data.address_line1} onChange={(v) => set('address_line1', v)} testid="ngs-addr1" /></F>
          <F label="Address Line 2"><I value={data.address_line2 || ''} onChange={(v) => set('address_line2', v)} testid="ngs-addr2" /></F>
          <F label="Address Line 3"><I value={data.address_line3 || ''} onChange={(v) => set('address_line3', v)} testid="ngs-addr3" /></F>
          <F label="City"><I value={data.city} onChange={(v) => set('city', v)} testid="ngs-city" /></F>
          <F label="State"><I value={data.state} onChange={(v) => set('state', v)} testid="ngs-state" /></F>
          <F label="PIN"><I value={data.pin_code} onChange={(v) => set('pin_code', v)} testid="ngs-pin" /></F>
        </Grid>
      </Section>

      <Section title="UPI & QR Code (auto-filled amount on Scan)" icon={<QrCode size={16} />}>
        <Grid>
          <F label="UPI ID *" hint="Customers scanning the receipt QR will pay to this VPA"><I value={data.upi_id} onChange={(v) => set('upi_id', v)} testid="ngs-upi" /></F>
          <F label="UPI Display Name"><I value={data.upi_display_name} onChange={(v) => set('upi_display_name', v)} testid="ngs-upi-name" /></F>
          <F label="GPay Number"><I value={data.gpay_number || ''} onChange={(v) => set('gpay_number', v)} testid="ngs-gpay" /></F>
        </Grid>
        <div className="mt-3 max-w-md">
          <div className="text-xs font-semibold text-slate-700 mb-1">Custom QR Image (optional)</div>
          <ImageField value={data.qr_image_url} onUpload={(f) => uploadAsset('qr', 'qr_image_url', f)} onClear={() => set('qr_image_url', '')}
            uploading={uploading === 'qr'} testid="ngs-qr" hint="If set, replaces the auto-generated UPI QR with your branded QR image." />
        </div>
      </Section>

      <Section title="Bank Details (NEFT / IMPS / RTGS)" icon={<Building2 size={16} />}>
        <Grid>
          <F label="Bank Name"><I value={data.bank_name} onChange={(v) => set('bank_name', v)} testid="ngs-bank" /></F>
          <F label="Account Name"><I value={data.bank_account_name} onChange={(v) => set('bank_account_name', v)} testid="ngs-acc-name" /></F>
          <F label="Account Number"><I value={data.bank_account_no} onChange={(v) => set('bank_account_no', v)} testid="ngs-acc-no" /></F>
          <F label="IFSC"><I value={data.bank_ifsc} onChange={(v) => set('bank_ifsc', v.toUpperCase())} testid="ngs-ifsc" /></F>
          <F label="Branch"><I value={data.bank_branch} onChange={(v) => set('bank_branch', v)} testid="ngs-branch" /></F>
        </Grid>
      </Section>

      <Section title="Signatures & Stamp" icon={<FileSignature size={16} />}>
        <Grid>
          <F label="Signatory Name"><I value={data.signatory_name} onChange={(v) => set('signatory_name', v)} testid="ngs-sig-name" /></F>
          <F label="Signatory Designation"><I value={data.signatory_designation} onChange={(v) => set('signatory_designation', v)} testid="ngs-sig-desg" /></F>
        </Grid>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Signature Image (PNG transparent)</div>
            <ImageField value={data.signature_url} onUpload={(f) => uploadAsset('signature', 'signature_url', f)} onClear={() => set('signature_url', '')}
              uploading={uploading === 'signature'} testid="ngs-sig" hint="Prints above the signatory name." />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Company Stamp Image (PNG transparent)</div>
            <ImageField value={data.stamp_url} onUpload={(f) => uploadAsset('stamp', 'stamp_url', f)} onClear={() => set('stamp_url', '')}
              uploading={uploading === 'stamp'} testid="ngs-stamp" hint="Prints next to the signature." />
          </div>
        </div>
      </Section>

      <Section title="Accepted Payment Methods" icon={<Wallet size={16} />}>
        <div className="flex flex-wrap gap-2" data-testid="ngs-methods">
          {METHOD_OPTIONS.map((m) => {
            const on = (data.payment_methods || []).includes(m.id);
            return (
              <button key={m.id} type="button" onClick={() => toggleMethod(m.id)} data-testid={`ngs-method-${m.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${on ? 'bg-emerald-600 text-white shadow' : 'bg-white border border-slate-300 text-slate-700 hover:border-emerald-400'}`}>
                {m.label}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Terms & Notes (prints at the bottom of the receipt)" icon={<FileText size={16} />}>
        <textarea value={data.terms_notes || ''} onChange={(e) => set('terms_notes', e.target.value)} data-testid="ngs-terms"
          rows={5} placeholder={'e.g.\n1. This is a Non-GST Bill of Supply.\n2. Goods/services once sold are not returnable.\n3. All disputes subject to Dehradun jurisdiction.'}
          className="w-full rounded-lg border border-slate-300 focus:border-emerald-500 outline-none text-sm p-3 leading-relaxed" />
        <p className="text-[10px] text-slate-500 mt-1">Each new line prints as a separate line on the receipt. Leave blank to hide this section.</p>
      </Section>

      <div className="text-xs text-slate-400">Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString('en-IN') : 'never'}</div>
    </div>
  );
}

function ImageField({ value, onUpload, onClear, uploading, testid, hint }) {
  const src = value ? (value.startsWith('http') ? value : `${BACKEND}${value}`) : '';
  return (
    <div className="flex items-center gap-3" data-testid={`${testid}-wrap`}>
      <div className="h-16 w-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
        {src ? <img src={src} alt="asset" className="max-h-full max-w-full object-contain" data-testid={`${testid}-preview`} />
          : <ImageIcon size={20} className="text-slate-300" />}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 cursor-pointer hover:border-emerald-400" data-testid={`${testid}-upload`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : (value ? 'Replace' : 'Upload')}
            <input type="file" accept="image/*" className="hidden" disabled={uploading}
              onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          {value && (
            <button type="button" onClick={onClear} className="inline-flex items-center gap-1 px-2.5 h-9 rounded-lg text-sm text-rose-600 hover:bg-rose-50" data-testid={`${testid}-clear`}>
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
        {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">{icon}{title}</h3>
      {children}
    </section>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>; }
function F({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
      {hint && <div className="text-[10px] text-slate-500 mb-1">{hint}</div>}
      {children}
    </label>
  );
}
function I({ value, onChange, type = 'text', maxLength, testid }) {
  return (
    <input type={type} value={value ?? ''} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-10 w-full px-3 rounded-lg border border-slate-300 focus:border-emerald-500 outline-none text-sm" />
  );
}
